import { DocumentSignLedgerStatus } from '@/lib/api';

/**
 * Per-event badge surfacing the ledger anchor state. v1 ships every
 * anchored event in `disabled` (the chain isn't wired) — once the REST
 * client lands the same UI will start rendering yellow → blue → green as
 * events flow through pending → submitted → confirmed.
 *
 * Color contrast targets WCAG AA on both light and dark themes; we lean on
 * the Tailwind palette + opacity so the badge survives outside this file.
 */

interface LedgerBadgeProps {
  status: DocumentSignLedgerStatus;
  ledgerRef?: string | null;
}

const STYLES: Record<DocumentSignLedgerStatus, { label: string; className: string }> = {
  disabled: { label: 'Off-chain', className: 'bg-muted text-muted-foreground' },
  pending: { label: 'Pending', className: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300' },
  submitted: { label: 'Submitted', className: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  confirmed: {
    label: 'Confirmed',
    className: 'bg-green-500/15 text-green-700 dark:text-green-300',
  },
  failed: { label: 'Failed', className: 'bg-red-500/15 text-red-700 dark:text-red-300' },
};

export function LedgerBadge({ status, ledgerRef }: LedgerBadgeProps) {
  const style = STYLES[status];
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${style.className}`}
      title={ledgerRef ? `Ledger ref: ${ledgerRef}` : `Ledger: ${style.label}`}
    >
      {style.label}
    </span>
  );
}
